import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import logo72 from "@/assets/nickrobotic-logo.webp?w=72&format=webp";
import logo144 from "@/assets/nickrobotic-logo.webp?w=144&format=webp";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useApp } from "@/hooks/useApp";
import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import {
  SunIcon, MoonIcon, LanguagesIcon, WalletIconSvg, UserIcon, LogOutIcon,
  LayoutDashboardIcon, BotIcon, ShieldCheckIcon, ShoppingBagIcon,
} from "@/components/icons/HeaderIcons";
import { preloadIntent } from "@/lib/preloadIntent";
import { ACTIVE_SOCIAL_LINKS } from "@/lib/social";
import NotificationBell from "@/components/NotificationBell";

export default function Layout() {
  const { user, isAdmin, signOut } = useAuth();
  const { t, lang, setLang, theme, setTheme } = useApp();
  const nav = useNavigate();
  const [balance, setBalance] = useState<number | null>(null);
  const [isSupplier, setIsSupplier] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) { setBalance(null); return; }
    let active = true;
    let cleanupChannel: (() => void) | null = null;

    const start = async () => {
      // Dynamic import keeps the supabase chunk out of the initial bundle on
      // anonymous first-paint and only loads it once the user is signed in.
      const { supabase } = await import("@/integrations/supabase/client");
      if (!active) return;
      const { data } = await supabase.from("wallets").select("balance").eq("user_id", userId).maybeSingle();
      if (active) setBalance(Number(data?.balance ?? 0));
      const channel = supabase
        .channel(`wallet-badge-${userId}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "wallets", filter: `user_id=eq.${userId}` }, (payload: any) => {
          if (active) setBalance(Number(payload.new?.balance ?? 0));
        })
        .subscribe();
      cleanupChannel = () => { supabase.removeChannel(channel); };
    };

    const w: any = window;
    const trigger = () => { start(); };
    let idleId: any;
    let timeoutId: any;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(trigger, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(trigger, 800);
    }
    return () => {
      active = false;
      if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
      cleanupChannel?.();
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) { setIsSupplier(false); return; }
    let cancel = false;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.from("suppliers").select("id").eq("user_id", userId).maybeSingle();
      if (!cancel) setIsSupplier(!!data);
    })();
    return () => { cancel = true; };
  }, [userId]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="container flex h-16 items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg min-w-0 shrink">
            <img
              src={logo72}
              srcSet={`${logo72} 1x, ${logo144} 2x`}
              width={36}
              height={36}
              alt="NickRobotic"
              decoding="async"
              className="w-9 h-9 rounded-lg object-cover shrink-0"
              style={{ boxShadow: "var(--shadow-glow)" }}
            />
            <span className="gradient-text truncate">NickRobotic</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 text-sm">
            <NavLink to="/" end className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{t("nav_home")}</NavLink>
            <NavLink to="/produtos" {...preloadIntent("products")} className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{t("nav_products")}</NavLink>
            <NavLink to="/servicos" {...preloadIntent("services")} className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{lang === "en" ? "Services" : "Serviços"}</NavLink>
            {user && <NavLink to="/meus-pedidos" {...preloadIntent("myOrders", "supabase")} className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{t("nav_orders")}</NavLink>}
            {user && <NavLink to="/meus-servicos" {...preloadIntent("supabase")} className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{lang === "en" ? "My Services" : "Meus Serviços"}</NavLink>}
            {user && <NavLink to="/suporte" {...preloadIntent("supabase")} className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{t("nav_support")}</NavLink>}
            <NavLink to="/tutoriais" className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{lang === "en" ? "Tutorials" : "Tutoriais"}</NavLink>
            {isAdmin && <NavLink to="/admin" className={({ isActive }) => isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}>{t("nav_admin")}</NavLink>}
          </nav>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" variant="ghost" onClick={() => setLang(lang === "pt" ? "en" : "pt")} title="Language" aria-label={lang === "en" ? "Change language" : "Mudar idioma"} className="hidden sm:inline-flex">
              <LanguagesIcon className="w-4 h-4" /> <span className="ml-1 text-xs uppercase">{lang}</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} title="Theme" aria-label={lang === "en" ? "Toggle theme" : "Alternar tema"}>
              {theme === "dark" ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
            </Button>
            {user ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => nav("/carteira")}
                  className="gap-1.5 h-9 px-2.5 border-primary/40 hover:border-primary"
                  title={t("wallet_title")}
                >
                  <WalletIconSvg className="w-4 h-4 text-primary" />
                  <span className="text-xs font-semibold tabular-nums">
                    R$ {balance == null ? "—" : balance.toFixed(2)}
                  </span>
                </Button>
                <NotificationBell />
                {isAdmin && <Button size="sm" variant="ghost" onClick={() => nav("/admin")} aria-label={lang === "en" ? "Admin dashboard" : "Painel administrativo"} className="hidden md:inline-flex"><LayoutDashboardIcon className="w-4 h-4" /></Button>}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" aria-label={lang === "en" ? "My account" : "Minha conta"} className="hidden md:inline-flex"><UserIcon className="w-4 h-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-popover z-50">
                    <DropdownMenuItem onClick={() => nav("/perfil")}>{lang === "en" ? "Profile" : "Perfil"}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/meus-pedidos")}>{t("nav_orders")}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/meus-servicos")}>{lang === "en" ? "My Services" : "Meus Serviços"}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/carteira")}>{t("wallet_title")}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/conta/api")}>API</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/tutoriais/api-revendedor-nickrobotic-guia-completo")}>{lang === "en" ? "API Tutorial" : "Tutorial API"}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/suporte")}>{t("nav_support")}</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {(isSupplier || isAdmin) && <DropdownMenuItem onClick={() => nav("/supplier")}>{lang === "en" ? "Supplier panel" : "Painel fornecedor"}</DropdownMenuItem>}
                    {!isSupplier && !isAdmin && <DropdownMenuItem onClick={() => nav("/seja-supplier")}>{lang === "en" ? "Become a supplier" : "Seja um fornecedor"}</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { await signOut(); nav("/"); }}>
                      <LogOutIcon className="w-4 h-4 mr-2" />{lang === "en" ? "Sign out" : "Sair"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button size="sm" onClick={() => nav("/auth")}>{t("btn_signin")}</Button>
            )}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button size="sm" variant="ghost" className="lg:hidden" aria-label="Menu"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <nav className="flex flex-col gap-1 mt-8 text-base">
                  {[
                    { to: "/", label: t("nav_home"), end: true, show: true },
                    { to: "/produtos", label: t("nav_products"), show: true },
                    { to: "/servicos", label: lang === "en" ? "Services" : "Serviços", show: true },
                    { to: "/meus-pedidos", label: t("nav_orders"), show: !!user },
                    { to: "/perfil", label: lang === "en" ? "Profile" : "Perfil", show: !!user },
                    { to: "/meus-servicos", label: lang === "en" ? "My Services" : "Meus Serviços", show: !!user },
                    { to: "/suporte", label: t("nav_support"), show: !!user },
                    { to: "/carteira", label: t("wallet_title"), show: !!user },
                    { to: "/conta/api", label: "API", show: !!user },
                    { to: "/tutoriais", label: lang === "en" ? "Tutorials" : "Tutoriais", show: true },
                    { to: "/seja-supplier", label: lang === "en" ? "Become a supplier" : "Seja um fornecedor", show: !!user && !isSupplier && !isAdmin },
                    { to: "/supplier", label: lang === "en" ? "Supplier panel" : "Painel fornecedor", show: !!user && (isSupplier || isAdmin) },
                    { to: "/admin", label: t("nav_admin"), show: isAdmin },
                  ].filter(i => i.show).map(i => (
                    <NavLink key={i.to} to={i.to} end={i.end} onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `px-3 py-2 rounded-lg ${isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-secondary"}`}>
                      {i.label}
                    </NavLink>
                  ))}
                  <div className="border-t border-border my-3" />
                  <div className="flex items-center gap-2 px-3">
                    <Button size="sm" variant="outline" onClick={() => setLang(lang === "pt" ? "en" : "pt")}>
                      <LanguagesIcon className="w-4 h-4" /> <span className="ml-1 text-xs uppercase">{lang}</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                      {theme === "dark" ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
                    </Button>
                    {user && (
                      <Button size="sm" variant="outline" onClick={async () => { await signOut(); setMobileOpen(false); nav("/"); }}>
                        <LogOutIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="border-t border-border py-10 mt-16">
        <div className="container text-center text-sm text-muted-foreground space-y-4">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <span className="flex items-center gap-1"><BotIcon className="w-4 h-4 text-primary" /> {t("bf_instant")}</span>
            <span className="flex items-center gap-1"><ShieldCheckIcon className="w-4 h-4 text-primary" /> {t("secure_pay")}</span>
            <span className="flex items-center gap-1"><ShoppingBagIcon className="w-4 h-4 text-primary" /> {t("bf_access")}</span>
          </div>
          <nav className="flex items-center justify-center gap-4 flex-wrap text-xs">
            <Link to="/sobre" className="hover:text-foreground">{lang === "en" ? "About" : "Sobre"}</Link>
            <Link to="/parceiros" className="hover:text-foreground">{lang === "en" ? "Partners" : "Parceiros"}</Link>
            <Link to="/politicas" className="hover:text-foreground">{lang === "en" ? "Policies" : "Políticas"}</Link>
            <Link to="/suporte" className="hover:text-foreground">{t("nav_support")}</Link>
            <Link to="/#faq" className="hover:text-foreground">FAQ</Link>
            <Link to="/produtos" className="hover:text-foreground">{t("nav_products")}</Link>
            <Link to="/servicos" className="hover:text-foreground">{lang === "en" ? "Services" : "Serviços"}</Link>
            <Link to="/tutoriais" className="hover:text-foreground">{lang === "en" ? "Tutorials" : "Tutoriais"}</Link>
            <Link to="/seja-supplier" className="hover:text-foreground">{lang === "en" ? "Become a supplier" : "Seja um supplier"}</Link>
          </nav>
          {ACTIVE_SOCIAL_LINKS.length > 0 && (
            <div className="flex items-center justify-center gap-2 flex-wrap pt-1">
              {ACTIVE_SOCIAL_LINKS.map(({ name, url, Icon }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer me"
                  aria-label={lang === "en" ? `NickRobotic on ${name}` : `NickRobotic no ${name}`}
                  className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
                >
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </a>
              ))}
            </div>
          )}
          <div>© {new Date().getFullYear()} NickRobotic — {t("footer_rights")}</div>
        </div>
      </footer>
    </div>
  );
}
